import { Building2, ChevronsUpDown, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBusiness } from "@/contexts/BusinessContext";
import { useNavigate } from "react-router-dom";
import { resolveAsset, hideOnError } from "@/lib/assets";

// Small tile that renders either the uploaded logo image or a coloured initial
// as fallback. Reused inside the trigger button and each dropdown item so we
// only maintain one visual style for a business identity across the app.
function BizAvatar({ biz, sizeClass = "h-5 w-5", textSize = "text-[10px]" }) {
  return (
    <div
      className={`${sizeClass} rounded flex items-center justify-center ${textSize} font-bold text-white shrink-0 overflow-hidden`}
      style={{ background: biz?.brand_color || "#FE7F2D" }}
    >
      {biz?.logo ? (
        <img src={resolveAsset(biz.logo)} alt={biz.name} className="h-full w-full object-cover" onError={hideOnError} />
      ) : (
        biz?.name?.[0]?.toUpperCase() || "B"
      )}
    </div>
  );
}

export function BusinessSwitcher() {
  const { businesses, activeBusiness, switchBusiness } = useBusiness();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 min-w-[180px] justify-between"
          data-testid="business-switcher-trigger"
        >
          <div className="flex items-center gap-2 truncate">
            <BizAvatar biz={activeBusiness} sizeClass="h-5 w-5" textSize="text-[10px]" />
            <span className="truncate text-sm">{activeBusiness?.name || "Select business"}</span>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="start">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Your Businesses
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {businesses.map((b) => (
          <DropdownMenuItem
            key={b.id}
            onSelect={() => switchBusiness(b.id)}
            data-testid={`biz-option-${b.slug}`}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2 w-full">
              <BizAvatar biz={b} sizeClass="h-6 w-6" textSize="text-[11px]" />
              <span className="flex-1 truncate">{b.name}</span>
              {b.id === activeBusiness?.id && <Check className="h-4 w-4 text-accent" />}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate("/settings/businesses")}
          data-testid="biz-manage-link"
          className="cursor-pointer"
        >
          <Plus className="h-4 w-4 mr-2" /> Manage businesses
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
